import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';

import { AppState } from 'src/app/state';
import { DashboardCamelCasePipe } from 'src/app/core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';
import { DashboardSnakeCasePipe } from 'src/app/core/pipes/dashboard-snake-case/dashboard-snake-case.pipe';
import { SubsectionsService } from 'src/app/dashboard/services/subsections/subsections.service';
import * as subsectionActions from './subsections.actions';
import { Subsection } from 'src/app/models/subsection';
import * as barActions from '../bars/bars.actions';
import { selectSelectedSection } from '../sections/sections.selectors';
import { SectionType } from 'src/app/models/section';

@Injectable()
export class SubsectionEffects {
  setSelectedSubsection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(subsectionActions.setSelectedSubsection),
      switchMap(({ selectedSubsection }) => {
        return [
          selectedSubsection?.bars
            ? barActions.getBars({
                bars: selectedSubsection.bars,
              })
            : barActions.getBars({ bars: [] }),
        ];
      })
    )
  );

  createSubsection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(subsectionActions.createSubsection),
      switchMap(({ subsection }) => {
        const subsectionSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(subsection);
        return this.subsectionsService.createSubsection(subsectionSnakeCase).pipe(
          map((createdSubsection: Subsection) => {
            createdSubsection = {
              ...createdSubsection,
              detail: subsection.detail,
            };
            const createdSubsectionCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(createdSubsection);
            return subsectionActions.createSubsectionSuccess({ createdSubsection: createdSubsectionCamelCase });
          }),
          catchError((err: HttpErrorResponse) => {
            return of(subsectionActions.createSubsectionError({ err }));
          })
        );
      })
    )
  );

  createSubsectionError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(subsectionActions.createSubsectionError),
        tap(({ err }) => {
          this.toastrService.error('Unable to create new subsection');
        })
      ),
    { dispatch: false }
  );

  updateSubsection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(subsectionActions.updateSubsection),
      withLatestFrom(this.store.select(selectSelectedSection)),
      switchMap(([{ subsection }, selectedSection]) => {
        const subsectionSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(subsection);
        return this.subsectionsService.updateSubsection(subsectionSnakeCase).pipe(
          map((updatedSubsection: Subsection) => {
            if (selectedSection.type === SectionType.DIMENSION) {
              const bars = [];
              for (let i = 0; i < subsection.bars?.length; i++) {
                const bar = {
                  ...subsection.bars[i],
                  id: subsection.bars[i].id ? subsection.bars[i].id : updatedSubsection[1][i].id,
                };
                bars.push(bar);
              }

              updatedSubsection = {
                ...updatedSubsection,
                translations: subsection.translations,
                bars,
              };
            } else {
              updatedSubsection = {
                ...updatedSubsection,
                translations: subsection.translations,
                detail: subsection.detail,
              };
            }
            const updatedSubsectionCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(updatedSubsection);
            return subsectionActions.updateSubsectionSuccess({ updatedSubsection: updatedSubsectionCamelCase });
          }),
          catchError((err: HttpErrorResponse) => {
            return of(subsectionActions.updateSubsectionError({ err }));
          })
        );
      })
    )
  );

  updateSubsectionError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(subsectionActions.updateSubsectionError),
        tap(({ err }) => {
          this.toastrService.error('Unable to update subsection');
        })
      ),
    { dispatch: false }
  );

  deleteSubsection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(subsectionActions.deleteSubsection),
      switchMap(({ subsection }) => {
        const subsectionSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(subsection);
        return this.subsectionsService.deleteSubsection(subsectionSnakeCase).pipe(
          map(() => subsectionActions.deleteSubsectionSuccess({ deletedSubsection: subsection })),
          catchError((err: HttpErrorResponse) => of(subsectionActions.deleteSubsectionError({ err })))
        );
      })
    )
  );

  deleteSubsectionSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(subsectionActions.deleteSubsectionSuccess),
        tap(({ deletedSubsection }) => this.toastrService.success('Subsection deleted!'))
      ),
    { dispatch: false }
  );

  deleteSubsectionError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(subsectionActions.deleteSubsectionError),
        tap(({ err }) => this.toastrService.error('Unable to delete subsection'))
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private subsectionsService: SubsectionsService,
    private dashboardCamelCasePipe: DashboardCamelCasePipe,
    private dashboardSnakeCasePipe: DashboardSnakeCasePipe,
    private toastrService: ToastrService,
    private store: Store<AppState>
  ) {}
}
